<template>
  <div class="ecs-time-picker">
    <label class="hours">
      <input
        v-model="hours"
        class="time-input"
        placeholder="HH"
        maxlength="2"
        type="number"
        onfocus="this.select()"
        :dark="$vuetify.theme.dark"
        :disabled="disabled"
        @blur="validateHours"
        @focus="onFocus"
        @keypress="onKeypress"
      />
    </label>
    <label class="minutes">
      <input
        v-model="minutes"
        class="time-input"
        placeholder="mm"
        type="number"
        maxlength="2"
        onfocus="this.select();"
        :dark="$vuetify.theme.dark"
        :disabled="disabled"
        @blur="validateMinutes"
        @focus="onFocus"
        @keypress="onKeypress"
      />
    </label>

    <v-select
      v-if="!isTwentyFourFormat"
      v-model="meridian"
      class="meridian-select"
      :disabled="disabled"
      placeholder="AM"
      :items="['AM', 'PM']"
      flat
      solo
      @blur="$emit('blur')"
      @focus="onFocus"
    />
  </div>
</template>

<script>
export default {
  name: 'EcsTimePicker',

  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    format: {
      type: String,
      default: 'HH:mm'
    },
    value: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      hours: null,
      minutes: null,
      meridian: null
    };
  },

  computed: {
    isTwentyFourFormat() {
      return this.format === 'HH:mm';
    }
  },

  watch: {
    value(newValue) {
      if (newValue) {
        this.computeTimeFromValue(newValue);
      }
    },

    meridian(newValue) {
      this.setTime();
    }
  },

  mounted() {
    if (this.value) {
      this.computeTimeFromValue(this.value);
    }
  },

  methods: {
    computeTimeFromValue(value) {
      let hours, minutes, meridian;
      // eslint-disable-next-line prefer-const
      [hours, minutes] = value.split(':');

      if (!this.isTwentyFourFormat) {
        [minutes, meridian] = minutes.split(' ');
      }

      this.hours = hours;
      this.minutes = minutes;
      this.meridian = meridian;
    },

    onBlur() {
      this.$emit('blur');
    },

    onFocus() {
      this.$emit('focus');
    },

    onKeypress(event) {
      const { keyCode } = event;
      if (keyCode > 31 && (keyCode < 48 || keyCode > 57)) {
        event.preventDefault();
      }
    },

    setTime() {
      let time = `${this.hours}:${this.minutes}`;
      if (!this.isTwentyFourFormat) {
        time += ` ${this.meridian}`;
      }

      this.$emit('input', time);
      this.$emit('change', time);
    },

    validateHours({ target }) {
      const value = Number(target.value);

      if (value < 0 || isNaN(value)) {
        this.hours = !this.isTwentyFourFormat ? '01' : '00';
      }

      if (!isNaN(value)) {
        if (!this.isTwentyFourFormat) {
          if (value < 10) {
            this.hours = `0${value}`;
          } else if (value > 12) {
            this.hours = '12';
          }
        } else if (value > 23) {
          this.hours = '23';
        }
      }

      this.setTime();
      this.$emit('blur');
    },

    validateMinutes({ target }) {
      const value = Number(target.value);

      if (value <= 0 || isNaN(value)) {
        this.minutes = '00';
      }

      if (!isNaN(value)) {
        if (value < 10) {
          this.minutes = `0${value}`;
        }

        if (value > 59) {
          this.minutes = '59';
        }
      }

      this.setTime();
      this.$emit('blur');
    }
  }
};
</script>

<style scoped>
input {
  border: none !important;
  outline: none !important;
  color: initial;
  width: 24px;
  text-align: center;
  font-size: 14px;
  min-height: 32px;
  color: #000000;
}

input[dark] {
  color: #ffffff;
}

label.hours:after {
  content: ':';
}

.meridian-select {
  display: inline-flex;
  width: 50px;
  height: 21px;
  margin-left: 8px;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}
</style>

<style>
.meridian-select .v-input__control {
  min-height: 21px !important;
  height: 21px !important;
}

.meridian-select .v-input__slot {
  background: transparent !important;
  font-size: 14px !important;
  margin-bottom: 0 !important;
  padding: 0 !important;
}

.meridian-select .v-input__append-inner {
  display: none;
}

.time-input:focus,
.meridian-select.v-input--is-focused .v-select__selection.v-select__selection--comma {
  text-shadow: none;
}
</style>
